import { useMemo, useState } from 'react';
import {
  AREA_OPACITY,
  AXIS,
  CHART_SURFACE,
  GRIDLINE,
  INK_MUTED,
  formatCompact,
  formatDay,
  niceTicks,
} from './chartTokens';
import { useElementWidth } from './useElementWidth';

export interface SeriesPoint {
  date: string;
  value: number;
}

const PAD = { top: 12, right: 16, bottom: 24, left: 44 };
const HEIGHT = 200;

/**
 * One measure over time, as a line with an area wash.
 *
 * Deliberately ONE series per chart. Events and active users differ by an order of magnitude, and
 * putting them on one plot would need two y-scales — the single most misleading thing a chart can
 * do, because the crossing point is an artefact of the scales rather than the data. Two charts
 * side by side answer the same question honestly.
 *
 * No legend: with one series the heading already says what is plotted, and a one-swatch legend box
 * just restates it.
 */
export function TimeSeriesChart({
  points,
  color,
  seriesLabel,
}: {
  points: SeriesPoint[];
  color: string;
  /** Names the measure in the tooltip and for screen readers. */
  seriesLabel: string;
}) {
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const plotWidth = Math.max(width - PAD.left - PAD.right, 10);
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;

  const { max, ticks, coords } = useMemo(() => {
    const highest = points.reduce((acc, p) => Math.max(acc, p.value), 0);
    // A flat all-zero series still needs a scale, or every point lands on the baseline and the
    // chart looks broken rather than empty.
    const tickValues = niceTicks(highest > 0 ? highest : 1);
    // Scale to the TOP TICK, not the raw maximum: otherwise the peak is pinned to the top edge
    // and the highest gridline would have to be drawn outside the plot.
    const scaleMax = tickValues[tickValues.length - 1] || 1;
    const step = points.length > 1 ? plotWidth / (points.length - 1) : 0;
    return {
      max: scaleMax,
      ticks: tickValues,
      coords: points.map((point, index) => ({
        ...point,
        x: PAD.left + index * step,
        y: PAD.top + plotHeight - (point.value / scaleMax) * plotHeight,
      })),
    };
  }, [points, plotWidth, plotHeight]);

  if (points.length === 0) return null;

  const linePath = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');
  const areaPath = `${linePath} L${coords[coords.length - 1].x},${PAD.top + plotHeight} L${coords[0].x},${PAD.top + plotHeight} Z`;
  const last = coords[coords.length - 1];
  const active = activeIndex === null ? null : coords[activeIndex];

  /** Snap to the nearest point: readers aim at a date, never at a 2px line. */
  const handleMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const step = points.length > 1 ? plotWidth / (points.length - 1) : plotWidth;
    const index = Math.round((x - PAD.left) / (step || 1));
    setActiveIndex(Math.min(Math.max(index, 0), points.length - 1));
  };

  const handleKey = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const current = activeIndex ?? points.length - 1;
    const next = event.key === 'ArrowLeft' ? current - 1 : current + 1;
    setActiveIndex(Math.min(Math.max(next, 0), points.length - 1));
  };

  return (
    <div ref={ref} className="relative w-full">
      <svg
        width={width}
        height={HEIGHT}
        role="img"
        tabIndex={0}
        aria-label={`${seriesLabel} per day. Use arrow keys to read individual days.`}
        className="touch-none outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2"
        onPointerMove={handleMove}
        onPointerLeave={() => setActiveIndex(null)}
        onKeyDown={handleKey}
        onBlur={() => setActiveIndex(null)}
      >
        {/* Gridlines: hairline, solid, recessive — they must never compete with the data. */}
        {ticks.map((tick) => {
          const y = PAD.top + plotHeight - (tick / max) * plotHeight;
          return (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={PAD.left + plotWidth}
                y1={y}
                y2={y}
                stroke={tick === 0 ? AXIS : GRIDLINE}
                strokeWidth={1}
              />
              <text
                x={PAD.left - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill={INK_MUTED}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatCompact(tick)}
              </text>
            </g>
          );
        })}

        <path d={areaPath} fill={color} opacity={AREA_OPACITY} />
        <path d={linePath} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

        {/* Crosshair. Drawn under the marks so it never obscures the value being read. */}
        {active && (
          <line
            x1={active.x}
            x2={active.x}
            y1={PAD.top}
            y2={PAD.top + plotHeight}
            stroke={AXIS}
            strokeWidth={1}
          />
        )}

        {/* End marker, and the hovered point. Both carry a 2px surface ring so they stay legible
            where they sit on the line. */}
        <circle cx={last.x} cy={last.y} r={4} fill={color} stroke={CHART_SURFACE} strokeWidth={2} />
        {active && active !== last && (
          <circle cx={active.x} cy={active.y} r={4} fill={color} stroke={CHART_SURFACE} strokeWidth={2} />
        )}

        {/* First and last day only — a tick under every column is unreadable at 90 days. */}
        <text x={PAD.left} y={HEIGHT - 6} fontSize={11} fill={INK_MUTED}>
          {formatDay(points[0].date)}
        </text>
        <text x={PAD.left + plotWidth} y={HEIGHT - 6} textAnchor="end" fontSize={11} fill={INK_MUTED}>
          {formatDay(points[points.length - 1].date)}
        </text>
      </svg>

      {/* The hovered/focused value, announced. Arrow keys move the selection, so a screen-reader
          user gets the same readout a pointer user gets from the tooltip — otherwise the keyboard
          path moves an invisible cursor and says nothing. */}
      <span aria-live="polite" className="sr-only">
        {active ? `${seriesLabel} ${active.value} on ${formatDay(active.date)}` : ''}
      </span>

      {/* Tooltip: the value leads, the label follows — the reader already knows the series and
          wants the number. React escapes both, so untrusted names cannot inject markup. */}
      {active && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 shadow-sm"
          style={{
            left: Math.min(Math.max(active.x, 60), Math.max(width - 60, 60)),
            top: Math.max(active.y - 52, 0),
          }}
        >
          <div className="text-sm font-semibold text-zinc-900">
            {active.value.toLocaleString('en-US')}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-zinc-500">
            <span className="inline-block h-0.5 w-3 rounded-full" style={{ backgroundColor: color }} />
            {seriesLabel} · {formatDay(active.date)}
          </div>
        </div>
      )}
    </div>
  );
}
